import { getLocale, tr } from "../i18n";

/**
 * Days and times as an owner reads them, in the device's language.
 *
 * The API sends a DATE column as "YYYY-MM-DD" — a day, not a moment — and a
 * TIME as "17:00:00". Handing the day to new Date() reads it as midnight UTC,
 * which is the evening before anywhere west of Greenwich, so days are always
 * built here in the browser's local time.
 */
const DAY_MS = 86400000;
const PLAIN_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A local-midnight Date for a day, from "YYYY-MM-DD" or a full timestamp. */
export const parseDay = (value) => {
  if (!value) return null;

  const match = PLAIN_DAY.exec(String(value));
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  const moment = new Date(value);
  if (Number.isNaN(moment.getTime())) return null;

  return new Date(moment.getFullYear(), moment.getMonth(), moment.getDate());
};

/**
 * "YYYY-MM-DD" for a local day — what the API's DATE filters take. Never
 * `toISOString()`, which is UTC: at 00:30 in Tunis that is still yesterday.
 */
export const isoDay = (date) => {
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const todayIso = () => isoDay(new Date());

export const monthStartIso = () => {
  const now = new Date();
  return isoDay(new Date(now.getFullYear(), now.getMonth(), 1));
};

/** The same day `days` earlier (or later, if negative), as "YYYY-MM-DD". */
export const daysAgoIso = (days) => {
  const now = new Date();
  return isoDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - days));
};

/** Whole days from today: 0 is today, 1 tomorrow, -1 yesterday. */
export const daysFromToday = (value) => {
  const day = parseDay(value);
  if (!day) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Rounded, because a day that crosses a clock change is 23 or 25 hours long
  return Math.round((day - today) / DAY_MS);
};

/** "Thu 10 Sep". */
export const formatDay = (value) => {
  const day = parseDay(value);
  if (!day) return "";

  return day.toLocaleDateString(getLocale(), {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

/** "10 Sep 2026" — a day where the year matters (history, reports). */
export const formatDate = (value) => {
  const day = parseDay(value);
  if (!day) return "";

  return day.toLocaleDateString(getLocale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/** "Today", "Tomorrow" or "Yesterday" when it is one of those, else the date. */
export const relativeDay = (value) => {
  const diff = daysFromToday(value);

  if (diff === 0) return tr("common.today");
  if (diff === 1) return tr("common.tomorrow");
  if (diff === -1) return tr("common.yesterday");
  return formatDay(value);
};

/** "17:00" from a TIME column. */
export const formatTime = (value) => (value ? String(value).slice(0, 5) : "");

/** The clock time of a full timestamp, such as when an order was placed. */
export const formatClock = (value) => {
  const moment = new Date(value);
  if (Number.isNaN(moment.getTime())) return "";

  return moment.toLocaleTimeString(getLocale(), {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Date and time of a full timestamp. */
export const formatDateTime = (value) => {
  const moment = new Date(value);
  if (Number.isNaN(moment.getTime())) return "";

  return moment.toLocaleString(getLocale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
